import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  AdvicePriceCalculationResult,
  AdviceStartPayload,
  AdviceState,
  GetEditedAkaAdvicePayload,
  OperationType,
} from "./interfaces/AkaAdviceState";
import { CollectiveAdvice, ProductName } from "./interfaces/CollectiveAdvice";

import { AkaAdvicePriceFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductPriceAka/AkaAdviceCards/interfaces";
import { ErrorStateOld } from "Store/interfaces/products";
import { isDefined } from "Utils/functions";

const initialState: AdviceState = {
  adviceResult: {
    current: null,
    isLoading: false,
    error: null,
  },
  advicePriceCalculationResults: {
    current: null,
    isLoading: false,
    error: null,
  },
};

const akaAdviceSlice = createSlice({
  name: "akaAdvice",
  initialState,
  reducers: {
    getAkaAdviceStart: (state, _action: PayloadAction<AdviceStartPayload>) => {
      state.adviceResult.isLoading = true;
    },
    getEditedAkaAdviceStart: (
      state,
      _action: PayloadAction<GetEditedAkaAdvicePayload>,
    ) => {
      state.adviceResult.isLoading = true;
    },
    getAkaAdviceSucceeded: (state, action: PayloadAction<CollectiveAdvice>) => {
      state.adviceResult.current = action.payload;
      state.adviceResult.isLoading = false;
      state.adviceResult.error = null;
    },
    akaAdvicePriceCalculationRequestStart: (
      state,
      _action: PayloadAction<{
        isCollectiveAdvice: boolean;
        priceFormValues: AkaAdvicePriceFormValues;
        specificProduct?: ProductName;
      }>,
    ) => {
      state.advicePriceCalculationResults.isLoading = true;
    },
    akaAdvicePriceCalculationRequestSucceeded: (
      state,
      action: PayloadAction<AdvicePriceCalculationResult[]>,
    ) => {
      state.advicePriceCalculationResults.current =
        action.payload.length === 1
          ? getAdvicePriceCalculationResults(state, action.payload[0])
          : action.payload;
      state.advicePriceCalculationResults.isLoading = false;
      state.advicePriceCalculationResults.error = null;
    },
    operationFailed: (
      state,
      action: PayloadAction<ErrorStateOld<OperationType>>,
    ) => {
      state.adviceResult.error = action.payload;
      state.adviceResult.isLoading = false;
      state.advicePriceCalculationResults.error = action.payload;
      state.advicePriceCalculationResults.isLoading = false;
    },
    resetAkaAdviceState: (state) => {
      Object.assign(state, initialState);
    },
  },
});

const getAdvicePriceCalculationResults = (
  state: AdviceState,
  payload: AdvicePriceCalculationResult,
): AdvicePriceCalculationResult[] => {
  const oldAdviceState = state.advicePriceCalculationResults.current;
  if (!isDefined(oldAdviceState)) {
    return [payload];
  }
  const currentResult = payload;
  const priceCalculationIndex = oldAdviceState.findIndex(
    (result) => result.configurationName === currentResult.configurationName,
  );
  const newAdviceState = [...oldAdviceState];
  if (priceCalculationIndex !== -1) {
    newAdviceState[priceCalculationIndex] = currentResult;
    return newAdviceState;
  }
  return oldAdviceState;
};

export const {
  akaAdvicePriceCalculationRequestStart,
  akaAdvicePriceCalculationRequestSucceeded,
  getAkaAdviceStart,
  getEditedAkaAdviceStart,
  getAkaAdviceSucceeded,
  operationFailed,
  resetAkaAdviceState,
} = akaAdviceSlice.actions;
export const akaAdvice = akaAdviceSlice.reducer;
